/* eslint-disable camelcase */
import { ELearningDto } from "../../models/elearning";
import { EventStatus } from "../../models/enums/eventStatus.enum";
import { EventType, InternalExternalEventType } from "../../models/enums/eventType.enum";
import { ParticipationStatus } from "../../models/enums/participationStatus.enum";
import { EventsSortBy } from "../../models/enums/sortBy.enum";
import { EventListData, UserLinkedAccountStats } from "../../models/eventData";
import { Pagination } from "../../models/pagination";
import { EventFilterService } from "../../services/filters/eventFiltersService";
import { TrainingType } from "../../models/enums/trainingType.enum";
import {
  ADD_EVENT_SUCCESS,
  ADD_TO_LIST_OF_EVENTS_SUCCESS,
  CLEAR_EDIT_EVENT,
  CLEAR_EVENT_LIST,
  DELETE_EVENT_SUCCESS,
  EDIT_EVENT_STATUS_SUCCESS,
  EventActionTypes,
  EventState,
  EVENTS_FAILURE,
  EVENTS_REQUEST,
  REFETCH_EVENTS_PAGE_SUCCESS,
  SET_EDIT_EVENT_INVOICE,
  SET_EDIT_EVENT_SUCCESS,
  SET_EVENTS_PAGE_COUNT,
  SET_EVENT_FAVORITE_STATUS,
  SET_FILTER_DATA,
  SET_FILTER_VALUES,
  SET_HAS_MORE_EVENTS,
  SET_LIST_OF_EVENTS_SUCCESS,
  SET_COURSE_LANGUAGES,
  SET_EVENT_SORTING_FILTER,
} from "./types";
import { NIL as NIL_UUID } from "uuid";
import { DiscountItem } from "../../models/discountData";
import { DEFAULT_CERTIFICATE_TYPE } from "../../constants";
import {
  CourseLanguageData,
  CourseSubtitleData,
  CourseTopicData,
  CourseTagsData,
} from "../../models/courseData";

export const initialEventState: EventState = {
  courseLanguages: [],
  eventList: [],
  addEvent: {
    vnr: "",
    title: "",
    location: "",
    city: "",
    zip_code: "",
    location_details: "",
    begin: new Date(),
    end: new Date(),
    score: 0,
    price: 0,
    min_seats: 0,
    max_seats: 0,
    description: "",
    training_content: "",
    status: EventStatus.Undefined,
    internal_external: InternalExternalEventType.Internal,
    catering: false,
    event_type: EventType.Undefined,
    training_type: TrainingType.DefaultEvent,
    participation_types: [],
    speakers: [],
    documents: [],
    commentary: "",
    tasks: [],
    template: null,
    discounts: [] as DiscountItem[],
    is_certification_selected: false,
    certificate_type: DEFAULT_CERTIFICATE_TYPE,
    course_language: {} as CourseLanguageData,
    course_subtitle: {} as CourseSubtitleData,
    topic: {} as CourseTopicData,
    tags: [] as CourseTagsData[],
    has_sign_language: false,
  },
  editEvent: {
    elearning_link: "",
    url: "",
    id: NIL_UUID,
    vnr: "",
    title: "",
    location: "",
    city: "",
    zip_code: "",
    location_details: "",
    begin: new Date(),
    end: new Date(),
    score: 0,
    price: 0,
    min_seats: 0,
    max_seats: 0,
    description: "",
    training_content: "",
    status: EventStatus.Undefined,
    internal_external: InternalExternalEventType.Internal,
    catering: false,
    event_type: EventType.Undefined,
    training_type: TrainingType.DefaultEvent,
    participation_types: [],
    speakers: [],
    documents: [],
    commentary: "",
    tasks: [],
    checklist: [],
    checklist_additional_info: "",
    evaluation_link: "",
    evaluation_link_speaker: "",
    template: null,
    max_seats_taken: false,
    duration_per_unit_in_minutes: 0,
    max_time_period_to_finish_elearning_module_in_weeks: 0,
    number_of_units: 0,
    pinboard_is_activated: false,
    publish_elearning_after_event: false,
    participant_authentication: true,
    discounts: [] as DiscountItem[],
    is_certification_selected: false,
    certificate_type: DEFAULT_CERTIFICATE_TYPE,
    course_language: {} as CourseLanguageData,
    course_subtitle: {} as CourseSubtitleData,
    topic: {} as CourseTopicData,
    tags: [] as CourseTagsData[],
    has_sign_language: false,
  },
  editEventLoaded: false,
  currentPage: 0,
  hasMoreItemsToLoad: true,
  isLoading: false,
  filterValues: {
    locations: [],
  },
  sortingFilter: "",
  filterData: {
    search: "",
    location: [],
    event_type: [],
    training_type: [],
    status: "",
    participation_types: [],
    sorting: EventsSortBy.BeginOldest,
    my_target_group: false,
    begin_gte: "",
    begin_lte: "",
    language_filters: [],
    thematic_filters: [],
    target_group_filters: [],
  },
  error: false,
  notFoundError: false,
};

export function getInitialEventState() {
  // ! persist the filter state after page change and set as initial filter values
  const filterService = new EventFilterService(
    initialEventState.currentPage,
    initialEventState.filterData
  );
  const initialFilterData = filterService.parseFiltersFromUrl();

  // set the initial filter data (get from url)
  const initialState: EventState = {
    ...initialEventState,
    filterData: initialFilterData,
  };

  return initialState;
}

function editEventStatusID(
  eventId: string,
  eventStatus: number,
  eventList: Pagination<EventListData>[]
): Pagination<EventListData>[] {
  const copiedEventList = [...eventList];
  for (let i = 0; i < copiedEventList.length; i++) {
    for (let j = 0; j < copiedEventList[i].results.length; j++) {
      if (copiedEventList[i].results[j].id === eventId) {
        copiedEventList[i].results[j].status = eventStatus;
        return copiedEventList;
      }
    }
  }
  return copiedEventList;
}

function setEventFavoriteStatus(
  eventId: string,
  favoriteStatus: boolean,
  eventList: Pagination<EventListData>[]
): Pagination<EventListData>[] {
  const copiedEventList = [...eventList];
  for (let i = 0; i < copiedEventList.length; i++) {
    for (let j = 0; j < copiedEventList[i].results.length; j++) {
      if (copiedEventList[i].results[j].id === eventId) {
        copiedEventList[i].results[j].is_favorite = favoriteStatus;
        return copiedEventList;
      }
    }
  }
  return copiedEventList;
}

function setEventInvoice(event: ELearningDto): ELearningDto {
  const eventEdit = { ...event };
  eventEdit.is_invoiced = true;
  return eventEdit;
}

export function eventReducer(
  state = getInitialEventState(),
  action: EventActionTypes
): EventState {
  switch (action.type) {
    case SET_EVENT_SORTING_FILTER:
      return {
        ...state,
        sortingFilter: action.payload,
      };
    case EVENTS_REQUEST:
      return {
        ...state,
        isLoading: true,
      };
    case REFETCH_EVENTS_PAGE_SUCCESS:
      // eslint-disable-next-line no-case-declarations
      const updatedEventList = state?.eventList?.map(
        (content, i) =>
          i === action.page
            ? { ...content, ...action.data }
            : { ...content, count: action.data.count } // use same object but update the count
      );
      return {
        ...state,
        eventList: updatedEventList,
        isLoading: false,
        error: false,
        notFoundError: false,
        hasMoreItemsToLoad: updatedEventList[action.page].next !== null,
      };
    case SET_COURSE_LANGUAGES:
      return {
        ...state,
        courseLanguages: action.data,
      };
    case ADD_TO_LIST_OF_EVENTS_SUCCESS:
      return {
        ...state,
        eventList: [...state.eventList, action.data],
        isLoading: false,
        error: false,
        notFoundError: false,
        // check if there is another page
        hasMoreItemsToLoad: action.data.next !== null,
      };
    case SET_LIST_OF_EVENTS_SUCCESS:
      return {
        ...state,
        eventList: [action.data],
        isLoading: false,
        error: false,
        notFoundError: false,
        // check if there is another page
        hasMoreItemsToLoad: action.data.next !== null,
      };
    case ADD_EVENT_SUCCESS:
      return {
        ...state,
        addEvent: action.data,
        isLoading: false,
        error: false,
        notFoundError: false,
      };
    case DELETE_EVENT_SUCCESS:
      return {
        ...state,
        eventList: initialEventState.eventList,
        editEvent: initialEventState.editEvent,
        editEventLoaded: initialEventState.editEventLoaded,
        isLoading: initialEventState.isLoading,
        error: false,
        notFoundError: false,
      };
    case SET_EDIT_EVENT_SUCCESS:
      return {
        ...state,
        editEvent: action.data,
        editEventLoaded: true,
        isLoading: false,
        error: false,
        notFoundError: false,
      };
    case EDIT_EVENT_STATUS_SUCCESS:
      return {
        ...state,
        eventList: [
          ...editEventStatusID(action.eventId, action.eventStatus, state.eventList),
        ],
        isLoading: false,
        error: false,
        notFoundError: false,
      };
    case CLEAR_EDIT_EVENT:
      return {
        ...state,
        editEvent: initialEventState.editEvent,
        editEventLoaded: false,
        isLoading: false,
        error: false,
        notFoundError: false,
      };
    case SET_EDIT_EVENT_INVOICE:
      return {
        ...state,
        editEvent: setEventInvoice(state.editEvent),
        isLoading: false,
        error: false,
        notFoundError: false,
      };
    case SET_EVENT_FAVORITE_STATUS:
      return {
        ...state,
        eventList: [
          ...setEventFavoriteStatus(action.eventId, action.status, state.eventList),
        ],
        isLoading: false,
        error: false,
        notFoundError: false,
      };
    case EVENTS_FAILURE:
      return {
        ...state,
        isLoading: false,
        error: true,
        notFoundError: action.notFoundError,
      };
    case SET_EVENTS_PAGE_COUNT:
      return {
        ...state,
        currentPage: action.pageCount,
      };
    case SET_HAS_MORE_EVENTS:
      return {
        ...state,
        hasMoreItemsToLoad: action.hasMoreEvents,
      };
    case CLEAR_EVENT_LIST:
      return {
        ...state,
        eventList: initialEventState.eventList,
        currentPage: initialEventState.currentPage,
        filterValues: initialEventState.filterValues,
        hasMoreItemsToLoad: initialEventState.hasMoreItemsToLoad,
        isLoading: initialEventState.isLoading,
        error: false,
        notFoundError: false,
      };
    case SET_FILTER_VALUES:
      return {
        ...state,
        filterValues: action.filterValues,
        isLoading: false,
        error: false,
        notFoundError: false,
      };
    case SET_FILTER_DATA:
      return {
        ...state,
        filterData: action.filterData,
        // this will trigger a reload
        hasMoreItemsToLoad: true,
        currentPage: 0,
      };
    default:
      return state;
  }
}

export default eventReducer;
